import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getData, postData } from "../Commonfunction/CommonFunction";
import { toast } from "react-toastify";
import { Field, Form, Formik } from "formik";
import "../../assets/style/Permissions.css";
import FormHeading from "../common/FormHeading";
import { MdOutlineSecurityUpdateGood } from "react-icons/md";

const initialPermissions = {
  Branch: {
    add: false,
    edit: false,
    delete: false,
    view: false,
    permission: false,
  },
  State: {
    add: false,
    edit: false,
    delete: false,
    view: false,
    permission: false,
  },
  City: {
    add: false,
    edit: false,
    delete: false,
    view: false,
    permission: false,
  },
  County: {
    add: false,
    edit: false,
    delete: false,
    view: false,
    permission: false,
  },
  Department: {
    add: false,
    edit: false,
    delete: false,
    view: false,
    permission: false,
  },
  Designation: {
    add: false,
    edit: false,
    delete: false,
    view: false,
    permission: false,
  },
  TaxHead: {
    add: false,
    edit: false,
    delete: false,
    view: false,
    permission: false,
  },
  FinancialYear: {
    add: false,
    edit: false,
    delete: false,
    view: false,
    permission: false,
  },
  Cutoffdate: {
    add: false,
    edit: false,
    delete: false,
    view: false,
    permission: false,
  },
  EmailTemplates: {
    add: false,
    edit: false,
    delete: false,
    view: false,
    permission: false,
  },
  Customers: {
    add: false,
    edit: false,
    delete: false,
    view: false,
    permission: false,
  },
  Employees: {
    add: false,
    edit: false,
    delete: false,
    view: false,
    permission: false,
  },
  Cpa: {
    add: false,
    edit: false,
    delete: false,
    view: false,
    permission: false,
  },
};

export default function Permissions() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const forEmployees = location.pathname.includes("/employee/permission");
  const [permissionData, setPermissionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch data function
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const request_url = forEmployees
        ? `/api/get-employee-permission/${id}`
        : `/api/get-cpa-permission/${id}`;
      const result = await getData(request_url);
      if (result.success) {
        setPermissionData(result.data);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.log("Error: ", error);
      toast.error("Failed to fetch permission data");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      const adjustedValues = adjustCheckboxValues(values);
      const request_url = forEmployees
        ? `/api/update-employee-permission/${id}`
        : `/api/update-cpa-permission/${id}`;
      const postDataResult = await postData(request_url, adjustedValues);
      if (postDataResult.success) {
        toast.success(postDataResult.message);
        navigate(forEmployees ? "/employees" : "/all-cpa");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred");
    }
  };

  // Adjust checkbox values
  const adjustCheckboxValues = (values) => {
    const adjustedValues = { ...values };
    Object.keys(adjustedValues).forEach((module) => {
      if (
        module !== "select_all" &&
        typeof adjustedValues[module] === "object"
      ) {
        Object.keys(adjustedValues[module]).forEach((permission) => {
          if (typeof adjustedValues[module][permission] !== "boolean") {
            adjustedValues[module][permission] = false;
          }
        });
      }
    });
    return adjustedValues;
  };

  // Group modules by category for better organization
  const moduleGroups = {
    "Master Data": ["Branch", "State", "City", "County", "Department", "Designation"],
    "Configuration": ["TaxHead", "FinancialYear", "Cutoffdate", "EmailTemplates"],
    "User Management": ["Customers", "Employees", "Cpa"]
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="permissions-container">
      {isLoading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <Formik
          initialValues={permissionData || initialPermissions}
          enableReinitialize={true}
          onSubmit={handleSubmit}
        >
          {({ values, isSubmitting, setFieldValue }) => (
            <Form>
              <div className="permissions-wrapper">
                {/* Header */}
                <div className="permissions-header">
                  <FormHeading
                    heading={forEmployees ? "Employee Permissions" : "CPA Permissions"}
                    path={forEmployees ? "/employees" : "/all-cpa"}
                  />
                </div>

                {/* Table Container */}
                <div className="table-container">
                  <table className="permission-table">
                    <thead>
                      <tr>
                        <th rowSpan="2">Module</th>
                        <th colSpan="5">
                          <div className="toggle-wrapper">
                            Permissions
                            <label className="toggle-switch">
                              <Field
                                type="checkbox"
                                name="select_all"
                                onClick={() => {
                                  const newValue = !values.select_all;
                                  Object.keys(values).forEach((key) => {
                                    if (
                                      key !== "select_all" &&
                                      typeof values[key] === "object"
                                    ) {
                                      setFieldValue(`${key}.add`, newValue);
                                      setFieldValue(`${key}.edit`, newValue);
                                      setFieldValue(`${key}.delete`, newValue);
                                      setFieldValue(`${key}.view`, newValue);
                                      if (
                                        key === "Cpa" ||
                                        key === "Employees"
                                      ) {
                                        setFieldValue(
                                          `${key}.permission`,
                                          newValue
                                        );
                                      }
                                    }
                                  });
                                  setFieldValue("select_all", newValue);
                                }}
                              />
                              <span className="slider"></span>
                            </label>
                          </div>
                        </th>
                      </tr>
                      <tr>
                        <th>View</th>
                        <th>Add</th>
                        <th>Edit</th>
                        <th>Delete</th>
                        <th>Permission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(moduleGroups).map(([groupName, modules]) => (
                        <React.Fragment key={groupName}>
                          {modules.map((moduleName) => (
                            <tr key={moduleName}>
                              <td>{moduleName}</td>
                              <td>
                                <label className="custom-checkbox">
                                  <Field type="checkbox" name={`${moduleName}.view`} />
                                  <span className="checkmark"></span>
                                </label>
                              </td>
                              <td>
                                <label className="custom-checkbox">
                                  <Field type="checkbox" name={`${moduleName}.add`} />
                                  <span className="checkmark"></span>
                                </label>
                              </td>
                              <td>
                                <label className="custom-checkbox">
                                  <Field type="checkbox" name={`${moduleName}.edit`} />
                                  <span className="checkmark"></span>
                                </label>
                              </td>
                              <td>
                                <label className="custom-checkbox">
                                  <Field type="checkbox" name={`${moduleName}.delete`} />
                                  <span className="checkmark"></span>
                                </label>
                              </td>
                              <td>
                                {moduleName === "Cpa" || moduleName === "Employees" ? (
                                  <label className="custom-checkbox">
                                    <Field
                                      type="checkbox"
                                      name={`${moduleName}.permission`}
                                    />
                                    <span className="checkmark"></span>
                                  </label>
                                ) : (
                                  <span>-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Submit Button */}
                <div className="form-footer">
                  <button
                    type="submit"
                   className="btn btn-submit"
                    disabled={isSubmitting}
                  >
                    <MdOutlineSecurityUpdateGood size={16} />
                    Update Permissions
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
}



